/**
 * LanguagePrompt — one-time banner that offers Spanish to first-time visitors
 * whose browser reports `navigator.languages[0]` starting with `es`.
 *
 * Rules:
 *  - Never shows if a `lbp_lang` cookie/localStorage value already exists
 *    (user made a choice).
 *  - Never shows if we've already prompted (`lbp_lang_prompted` flag).
 *  - Dismissing the banner writes `en` preference so we don't ask again.
 *  - Accepting flips i18n to `es`, persists `lbp_lang=es`, and stays on the
 *    same URL — no navigation.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, X } from 'lucide-react';
import { Button } from './ui/button';

const COOKIE = 'lbp_lang';
const PROMPTED = 'lbp_lang_prompted';

function readStorage(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeCookie(name, value, days = 365) {
  try {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${value}; expires=${expires}; path=/; SameSite=Lax`;
    localStorage.setItem(name, value);
  } catch {
    /* cookies/localStorage disabled */
  }
}

export default function LanguagePrompt() {
  const { i18n } = useTranslation();
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Already chose / already prompted → never show.
    if (readStorage(COOKIE) === 'en' && readStorage(PROMPTED)) return;
    if (readStorage(PROMPTED)) return;
    if (document.cookie.includes(`${COOKIE}=es`)) return;

    // Detect if any of the browser's preferred languages is Spanish.
    const langs = (navigator.languages && navigator.languages.length
      ? navigator.languages
      : [navigator.language]
    ).map((l) => (l || '').toLowerCase());

    const primary = langs[0] || '';
    const currentI18n = (i18n.language || 'en').split('-')[0];
    const anyEs = langs.some((l) => l.startsWith('es'));
    const primaryEs = primary.startsWith('es');

    // Show the prompt when:
    //  - Primary browser language is Spanish but the UI is currently English
    //    (rare — means i18n didn't auto-detect for some reason), OR
    //  - Primary is English but Spanish is a secondary preference and UI is EN
    //    (common US-bilingual case — we don't flip automatically, we offer).
    if ((primaryEs && currentI18n !== 'es') || (!primaryEs && anyEs && currentI18n !== 'es')) {
      const t = setTimeout(() => setShow(true), 1200);
      return () => clearTimeout(t);
    }
  }, [i18n.language]);

  if (!show) return null;

  const accept = () => {
    writeCookie(COOKIE, 'es');
    writeCookie(PROMPTED, '1');
    i18n.changeLanguage('es');
    setShow(false);
  };

  const dismiss = () => {
    writeCookie(COOKIE, 'en');
    writeCookie(PROMPTED, '1');
    setShow(false);
  };

  return (
    <div
      className="fixed inset-x-0 bottom-4 z-[60] flex justify-center px-4 pointer-events-none"
      role="dialog"
      aria-live="polite"
      data-testid="language-prompt"
    >
      <div className="bg-white border border-slate-200 shadow-xl rounded-full px-4 sm:px-5 py-2.5 flex items-center gap-3 pointer-events-auto animate-[slideUp_0.3s_ease-out] max-w-lg">
        <Globe className="h-4 w-4 text-blue-600 shrink-0" />
        <p className="text-sm text-slate-700 flex-1">
          <span className="font-medium">¿Prefieres español?</span>{' '}
          <span className="text-slate-500">Tenemos el sitio completo en español.</span>
        </p>
        <Button
          size="sm"
          onClick={accept}
          className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-3 py-1 h-auto text-xs"
          data-testid="language-prompt-accept"
        >
          Sí, cambiar
        </Button>
        <button
          onClick={dismiss}
          className="text-slate-400 hover:text-slate-600"
          aria-label="Dismiss"
          data-testid="language-prompt-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
